import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';

/**
 * Global Keyboard Focus Management
 * 
 * Ensures that when an input or textarea is focused on mobile (especially iOS/Capacitor),
 * it smoothly scrolls into the center of the visible viewport after the virtual keyboard opens.
 * 
 * This prevents the keyboard from covering inputs on tight or non-scrollable layouts,
 * providing a seamless, native-like user experience.
 */
const KEYBOARD_OPEN_CLASS = 'keyboard-open';

const isTextInput = (el: EventTarget | null): boolean => {
    if (!el || !(el instanceof Element)) return false;
    if (el.tagName === 'TEXTAREA') return true;
    if (el.tagName === 'INPUT') {
        const type = (el as HTMLInputElement).type;
        return !['button', 'submit', 'reset', 'checkbox', 'radio', 'file', 'image', 'color', 'range'].includes(type);
    }
    return (el as HTMLElement).isContentEditable === true;
};

export function useKeyboardFocus() {
    useEffect(() => {
        // Only activate on devices with a virtual keyboard:
        // - Native Capacitor apps (iOS/Android)
        // - Touch-only devices: (hover: none) and (pointer: coarse) reliably
        //   identifies phones/tablets without a physical keyboard, ignoring
        //   window width which would incorrectly trigger on desktop narrow views.
        const hasTouchKeyboard = typeof window !== 'undefined' &&
            window.matchMedia('(hover: none) and (pointer: coarse)').matches;
        const isMobile = Capacitor.isNativePlatform() || hasTouchKeyboard;

        if (!isMobile) return;

        // keyboard-open class is managed by init.ts on native platforms via Capacitor events.
        // On web mobile (browser/PWA) we manage it here via focus/blur.
        const isWebMobile = !Capacitor.isNativePlatform();

        let focusTimeout: NodeJS.Timeout;
        let blurTimeout: NodeJS.Timeout;

        const setKeyboardOpen = (open: boolean) => {
            if (open) document.documentElement.classList.add(KEYBOARD_OPEN_CLASS);
            else document.documentElement.classList.remove(KEYBOARD_OPEN_CLASS);
        };

        const handleFocusIn = (e: FocusEvent) => {
            const target = e.target as HTMLElement;

            if (!isTextInput(target)) return;

            if (isWebMobile) {
                clearTimeout(blurTimeout);
                setKeyboardOpen(true);
            }

            // Clear any existing timeouts to prevent jumping
            clearTimeout(focusTimeout);

            // Wait for the virtual keyboard to fully open and the webview to resize (Capacitor Resize.Body)
            // usually takes ~300ms on iOS
            focusTimeout = setTimeout(() => {
                if (document.activeElement === target) {
                    try {
                        target.scrollIntoView({
                            behavior: 'smooth',
                            block: 'center', // Centers the input vertically in the remaining visible area
                            inline: 'nearest'
                        });
                    } catch (err) {
                        // Fallback for older Safari versions that don't support smooth block center
                        target.scrollIntoView(true);
                    }
                }
            }, 350);
        };

        const handleFocusOut = () => {
            clearTimeout(focusTimeout);

            if (isWebMobile) {
                // Delay to avoid flicker when moving focus from one input to another
                blurTimeout = setTimeout(() => {
                    if (!isTextInput(document.activeElement)) {
                        setKeyboardOpen(false);
                    }
                }, 100);
            }
        };

        // Use capture phase to ensure we catch focus events before they propagate
        document.addEventListener('focusin', handleFocusIn, { capture: true, passive: true });
        document.addEventListener('focusout', handleFocusOut, { capture: true, passive: true });

        return () => {
            clearTimeout(focusTimeout);
            clearTimeout(blurTimeout);
            document.removeEventListener('focusin', handleFocusIn, { capture: true });
            document.removeEventListener('focusout', handleFocusOut, { capture: true });
        };
    }, []);
}
